import { ActionContext, ActionTree, GetterTree, MutationTree } from "vuex";
import { RootState } from "@/store";
import * as listable from "./listableModule";
import cloneDeep from "lodash/cloneDeep";
import { UserForm, UserList } from "@/models/user";
import UserService from "@/services/userService";
import logger from "@/services/loggerService";
import i18n from "@/i18n";
import { AdminUserSearchSerializer } from "@/models/adminUser";

const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: string }).message;
    if (message) {
      return message;
    }
  }
  return typeof error === "string" ? error : "An unexpected error occurred";
};

export interface AdminUsersState extends listable.ListableState {
  users: UserList[];
  currentUser: UserList | undefined;
}

type AdminUsersContext = ActionContext<AdminUsersState, RootState>;

export const namespaced = true;

const initialState = {
  ...cloneDeep(listable.state),
  users: [] as UserList[],
  currentUser: undefined as UserList | undefined,
};

export const state = (): AdminUsersState => cloneDeep(initialState);

export const getters: GetterTree<AdminUsersState, RootState> = {
  ...listable.getters,
  users: (state): UserList[] => {
    return state.users;
  },
  currentUser: (state): UserList | undefined => {
    return state.currentUser;
  },
  isLoading: (state): boolean => {
    return state.loading;
  },
};

export const mutations: MutationTree<AdminUsersState> = {
  ...listable.mutations,
  setUsers(state: AdminUsersState, users: UserList[]) {
    state.users = users;
  },
  setCurrentUser(state: AdminUsersState, user: UserList | undefined) {
    state.currentUser = user;
  },
};

export const actions: ActionTree<AdminUsersState, RootState> = {
  ...listable.actions,
  async reset(context: AdminUsersContext): Promise<void> {
    const resetState = cloneDeep(initialState);
    context.state.users = resetState.users;
    context.state.currentUser = resetState.currentUser;
    context.dispatch("resetListable");
  },
  async fetchList(context: AdminUsersContext): Promise<void> {
    context.commit("setLoading", true);
    try {
      const query = AdminUserSearchSerializer(
        context.state.queryOptions,
        context.state.searchString,
        context.state.additionalFilters
      );
      const res = await UserService.searchAdmins(query);
      context.commit("setUsers", res.items as UserList[]);
      context.commit("setTotalItems", res.count);
    } catch (e) {
      logger.error(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  async createUser(
    context: AdminUsersContext,
    payload: { licenseKey: string; user: UserForm }
  ): Promise<boolean> {
    context.commit("setLoading", true);
    try {
      await UserService.post(payload.licenseKey, payload.user);
      context.commit(
        "notifications/displayNotification",
        {
          message: i18n.t("usersList.created"),
          type: "success",
        },
        { root: true }
      );
      await context.dispatch("fetchList");
      return true;
    } catch (e) {
      logger.error(e);
      context.commit(
        "notifications/displayNotification",
        {
          message: extractErrorMessage(e),
          type: "error",
        },
        { root: true }
      );
      return false;
    } finally {
      context.commit("setLoading", false);
    }
  },
  async updateUser(
    context: AdminUsersContext,
    payload: UserForm
  ): Promise<boolean> {
    context.commit("setLoading", true);
    try {
      await UserService.put(payload);
      context.commit(
        "notifications/displayNotification",
        {
          message: i18n.t("usersList.updated"),
          type: "success",
        },
        { root: true }
      );
      await context.dispatch("fetchList");
      return true;
    } catch (e) {
      logger.error(e);
      context.commit(
        "notifications/displayNotification",
        {
          message: extractErrorMessage(e),
          type: "error",
        },
        { root: true }
      );
      return false;
    } finally {
      context.commit("setLoading", false);
    }
  },
  async deleteUser(context: AdminUsersContext, id: string): Promise<boolean> {
    context.commit("setLoading", true);
    try {
      await UserService.delete(id);
      context.commit(
        "notifications/displayNotification",
        {
          message: i18n.t("usersList.deleted"),
          type: "success",
        },
        { root: true }
      );
      await context.dispatch("fetchList");
      return true;
    } catch (e) {
      logger.error(e);
      context.commit(
        "notifications/displayNotification",
        {
          message: extractErrorMessage(e),
          type: "error",
        },
        { root: true }
      );
      return false;
    } finally {
      context.commit("setLoading", false);
    }
  },
};
